import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCausas } from '@/hooks/useCausas';
import { CircleNotch, Pause } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

interface Props {
  /** Código de la obra a congelar; null = cerrado. */
  codigo: string | null;
  pending: boolean;
  onClose: () => void;
  onConfirmar: (causaCodigo: string, nota: string | null) => void;
}

/**
 * Diálogo para congelar una obra. Pide la causa (del catálogo, filtrada por
 * aplica_inactividad). El motivo queda registrado en obra_estado.motivo_inactiva.
 */
export function CongelarObraDialog({ codigo, pending, onClose, onConfirmar }: Props) {
  const causasQ = useCausas();
  const causas = (causasQ.data?.data ?? []).filter((c) => c.aplica_inactividad && c.activo);

  const [causaSel, setCausaSel] = useState<string | null>(null);
  const [nota, setNota] = useState('');

  useEffect(() => {
    if (codigo) {
      setCausaSel(null);
      setNota('');
    }
  }, [codigo]);

  const abierto = codigo !== null;

  function handleOpenChange(open: boolean) {
    if (!open && !pending) onClose();
  }

  function handleConfirmar() {
    if (!causaSel) return;
    onConfirmar(causaSel, nota.trim() || null);
  }

  return (
    <Dialog open={abierto} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pause className="h-5 w-5 text-amber-600" weight="fill" aria-hidden="true" />
            Congelar obra
          </DialogTitle>
          <DialogDescription>
            {codigo ? <span className="font-mono text-xs">{codigo}</span> : ''} — La obra queda en
            estado "en espera" y deja de aparecer activa en el tablero hasta que se reactive.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block text-xs">Motivo del congelamiento</Label>
            {causasQ.isLoading && (
              <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
                Cargando causas…
              </div>
            )}
            <div className="grid grid-cols-1 gap-2">
              {causas.map((c) => {
                const activa = causaSel === c.codigo;
                return (
                  <button
                    key={c.codigo}
                    type="button"
                    onClick={() => setCausaSel(c.codigo)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${
                      activa
                        ? 'border-amber-400 bg-amber-50 text-amber-900'
                        : 'border-border bg-white hover:bg-zinc-50'
                    }`}
                  >
                    <span className="font-mono text-xs text-muted-foreground">{c.codigo}</span>
                    <span>{c.descripcion}</span>
                  </button>
                );
              })}
            </div>
            {!causasQ.isLoading && causas.length === 0 && (
              <p className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
                No hay causas configuradas para inactividad. Pedile al admin que marque alguna como{' '}
                <code>aplica_inactividad</code> en el catálogo.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cong-nota" className="mb-1.5 block text-xs">
              Nota (opcional)
            </Label>
            <Textarea
              id="cong-nota"
              rows={2}
              placeholder="Detalle del motivo…"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            disabled={!causaSel || pending}
            className="bg-amber-600 text-white hover:bg-amber-700"
          >
            {pending ? (
              <>
                <CircleNotch
                  className="mr-2 h-4 w-4 animate-spin"
                  weight="bold"
                  aria-hidden="true"
                />
                Congelando…
              </>
            ) : (
              <>
                <Pause className="mr-1 h-4 w-4" weight="bold" aria-hidden="true" />
                Congelar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
